import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND_DEFAULTS, type PlatformBrand } from '@up2d8/contracts';
import { useSession } from '../../shared/session';
import { AdminIcon, Badge, BrandShield, Btn, Card, CardHead, CardPad, Count } from '../admin-kit';
import '../tenants/huisstijl.css';

/**
 * De waarde van CSS-variabelen op `<html>`, zoals de browser ze nu toepast.
 *
 * Opnieuw gelezen zodra de schil iets aan `<html>` verandert (een ander merk, `data-brand`): het
 * designsysteem toont het merk dat actief is, en niet een lijst die iemand ooit overtikte.
 */
export function useCssVars(names: readonly string[]): Record<string, string> {
  const key = names.join('|');
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const lijst = key.split('|');
    const read = () => {
      // Op een element in de beheerzone: daar gelden de AA-varianten van `admin.css`.
      const scope = document.querySelector('[data-zone]') ?? document.documentElement;
      const style = getComputedStyle(scope);
      setValues(
        Object.fromEntries(lijst.map((name) => [name, style.getPropertyValue(name).trim()])),
      );
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'data-brand', 'class'],
    });
    return () => observer.disconnect();
  }, [key]);

  return values;
}

const BRAND_TOKENS = [
  '--brand-primary',
  '--brand-primary-aa',
  '--brand-orange',
  '--brand-orange-aa',
  '--brand-dark-blue',
  '--brand-sidebar',
  '--a-primary',
  '--font-heading',
  '--font-body',
  '--radius-sm',
  '--radius-md',
  '--radius-lg',
] as const;

/**
 * "Actief merk" (MRK-3): de kernonderdelen in het merk dat nu op het platform staat.
 *
 * Echte onderdelen van de admin-kit en geen nagemaakte, met de tokens eronder zoals de browser ze
 * uitrekent. Zo is na een wijziging in Merk meteen te zien of knoppen, badge, kaart, zijbalk en
 * typografie het merk volgen - en waar niet.
 */
export function ActiveBrand() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const values = useCssVars(BRAND_TOKENS);

  const brand = useQuery({
    queryKey: ['platform-brand'],
    enabled: has('tenants.brand.manage'),
    queryFn: async (): Promise<PlatformBrand> => {
      const { data, error } = await api.GET('/api/v1/platform/brand');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as PlatformBrand;
    },
  });
  const productName = brand.data?.theme.productName ?? BRAND_DEFAULTS.productName;
  const logo = brand.data?.theme.productLogoDarkUrl ?? brand.data?.theme.productLogoUrl ?? null;

  return (
    <Card testId="active-brand">
      <CardHead title={`${t('beheer.merk.design.title')} · ${productName}`} />
      <CardPad>
        <p className="a-muted" style={{ margin: 0 }}>
          {t('beheer.merk.design.intro')}
        </p>
        <div className="a-grid two">
          <div className="a-stack">
            <h3 className="a-sect-title">{t('beheer.merk.design.buttons')}</h3>
            <div className="a-actions">
              <Btn icon={<AdminIcon name="check" size={16} />}>
                {t('beheer.merk.design.primary')}
              </Btn>
              <Btn variant="sec">{t('beheer.merk.design.secondary')}</Btn>
              <Btn variant="ghost">{t('beheer.merk.design.ghost')}</Btn>
              <Badge tone="solid">{t('beheer.merk.design.badge')}</Badge>
              <Badge tone="info">{t('beheer.merk.design.badge')}</Badge>
              <Count attention>3</Count>
            </div>
            <Card>
              <CardHead title={t('beheer.merk.design.cardTitle')} level={3} count={2} />
              <CardPad>
                <p className="a-muted" style={{ margin: 0 }}>
                  {t('beheer.merk.design.cardText')}
                </p>
              </CardPad>
            </Card>
          </div>
          <div className="a-stack">
            <h3 className="a-sect-title">{t('beheer.merk.design.sidebar')}</h3>
            {/* Een stuk van de echte zijbalk, met dezelfde klassen; zonder `.sidebar` zelf, want
                die is een rasterkolom van volle hoogte. */}
            <div
              className="a-brand-sidebar"
              style={{
                background: 'var(--brand-sidebar, var(--brand-dark-blue))',
                borderRadius: 'var(--radius-md)',
                width: '100%',
              }}
              data-testid="active-brand-sidebar"
            >
              <div className="a-brand-sidebar-head" style={{ fontFamily: 'var(--font-heading)' }}>
                {logo ? (
                  <img src={logo} alt="" />
                ) : (
                  <span className="sidebar-brand-shield" style={{ width: 28, height: 28 }}>
                    <BrandShield />
                  </span>
                )}
                <span>{productName}</span>
              </div>
              <span
                className="a-brand-sidebar-item on"
                style={{ borderRadius: 'var(--radius-sm)' }}
              >
                {t('beheer.merk.previewNavStart')}
              </span>
              <span className="a-brand-sidebar-item" style={{ borderRadius: 'var(--radius-sm)' }}>
                {t('beheer.merk.previewNavPosts')}
              </span>
            </div>
            <h3 className="a-sect-title">{t('beheer.merk.design.typography')}</h3>
            <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 20 }}>
              {t('beheer.merk.design.heading')}
            </div>
            <div style={{ fontFamily: 'var(--font-body)' }}>{t('beheer.merk.design.body')}</div>
          </div>
        </div>
      </CardPad>
      <div className="a-table-wrap">
        <table className="a-table" data-testid="active-brand-tokens">
          <thead>
            <tr>
              <th>{t('beheer.merk.design.token')}</th>
              <th>{t('beheer.merk.design.value')}</th>
            </tr>
          </thead>
          <tbody>
            {BRAND_TOKENS.map((name) => (
              <tr key={name}>
                <td>
                  <span className="a-inline" style={{ gap: 10 }}>
                    {name.startsWith('--font') || name.startsWith('--radius') ? null : (
                      <span
                        aria-hidden="true"
                        className="a-swatch"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          background: `var(${name})`,
                        }}
                      />
                    )}
                    <span className="a-mono">{name}</span>
                  </span>
                </td>
                <td className="a-mono">{values[name] || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
